import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Clock, MapPin, Route, Zap, Calendar, 
  Navigation, ChevronRight, CheckCircle, AlertTriangle 
} from 'lucide-react';
import { toast } from 'react-hot-toast';

const API = process.env.REACT_APP_BACKEND_URL;

const CreneauxDisponibles = ({ 
  isOpen, 
  onClose, 
  prospectAddress, 
  prospectLatitude, 
  prospectLongitude,
  onSelectCreneau 
}) => {
  const [loading, setLoading] = useState(false);
  const [creneauxOptimises, setCreneauxOptimises] = useState({});
  const [selectedCreneau, setSelectedCreneau] = useState(null);
  const [allRdv, setAllRdv] = useState([]);
  const [commerciaux, setCommerciaux] = useState([]);
  const [showAllSlots, setShowAllSlots] = useState({}); // Pour chaque commercial
  
  // 🚀 CACHE LOCAL (pas un state) pour éviter les géocodages répétés dans une même session
  const geocodeCacheRef = React.useRef({});

  useEffect(() => {
    if (isOpen && prospectLatitude && prospectLongitude) {
      calculateOptimalTimeSlots();
    }
  }, [isOpen, prospectLatitude, prospectLongitude]);

  // 🚀 FONCTION DE GÉOCODAGE AVEC CACHE (utilise useRef pour persister entre les renders)
  const geocodeAddressWithCache = async (fullAddress) => {
    // Vérifier le cache (useRef pour synchrone)
    if (geocodeCacheRef.current[fullAddress]) {
      console.log(`[Créneaux] 🎯 Cache hit pour: ${fullAddress}`);
      return geocodeCacheRef.current[fullAddress];
    }

    try {
      console.log(`[Créneaux] 📍 Géocodage de: ${fullAddress}`);
      const geocodeResponse = await fetch(
        `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(fullAddress)}&limit=1`
      );
      const geocodeData = await geocodeResponse.json();
      
      if (geocodeData.features && geocodeData.features.length > 0) {
        const coords = geocodeData.features[0].geometry.coordinates;
        const result = { lat: coords[1], lon: coords[0] };
        
        // Mettre en cache (useRef pour persister)
        geocodeCacheRef.current[fullAddress] = result;
        console.log(`[Créneaux] ✅ Géocodage réussi et mis en cache`);
        return result;
      }
    } catch (error) {
      console.error(`[Créneaux] ❌ Erreur géocodage:`, error);
    }
    
    return null;
  };

  const calculateOptimalTimeSlots = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      console.log('[Créneaux] 🚀 Début du calcul optimisé...');
      
      // 🚀 PARALLÉLISER les appels API
      const [rdvResponse, prospectsResponse, usersResponse] = await Promise.all([
        axios.get(`${API}/api/rendez-vous`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/prospects`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/users`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      const commerciauxData = usersResponse.data.filter(
        user => user.role === 'commercial' || user.role === 'regie'
      );
      setCommerciaux(commerciauxData);

      // Enrichir les RDV avec les données prospect
      const allRendezVous = rdvResponse.data
        .filter(rdv => rdv.assigned_to)
        .map(rdv => {
          const prospect = prospectsResponse.data.find(p => p.id === rdv.prospect_id);
          const commercial = commerciauxData.find(c => c.id === rdv.assigned_to);
          return {
            ...rdv,
            prospect: prospect || null,
            commercial: commercial || null
          };
        });

      // 🚀 GÉOCODER UNIQUEMENT LES RDV SANS COORDONNÉES (avec cache)
      const rdvsToGeocode = allRendezVous.filter(rdv => 
        (!rdv.prospect?.latitude || !rdv.prospect?.longitude) && 
        rdv.prospect?.adresse && 
        rdv.prospect?.code_postal && 
        rdv.prospect?.ville
      );

      console.log(`[Créneaux] 📍 ${rdvsToGeocode.length} prospects à géocoder`);

      // Géocoder en parallèle (mais limiter à 5 simultanés pour ne pas surcharger l'API)
      const geocodePromises = rdvsToGeocode.map(async (rdv) => {
        const fullAddress = `${rdv.prospect.adresse}, ${rdv.prospect.code_postal} ${rdv.prospect.ville}`;
        const coords = await geocodeAddressWithCache(fullAddress);
        
        if (coords && rdv.prospect) {
          rdv.prospect.latitude = coords.lat;
          rdv.prospect.longitude = coords.lon;
        }
      });

      // Traiter par batch de 5
      for (let i = 0; i < geocodePromises.length; i += 5) {
        await Promise.all(geocodePromises.slice(i, i + 5));
      }

      // Enrichir avec durée
      const enrichedRdv = allRendezVous.map(rdv => ({
        ...rdv,
        duree_minutes: rdv.duree_rdv_minutes || 60
      }));

      const rdvWithCoords = enrichedRdv.filter(rdv => rdv.prospect?.latitude && rdv.prospect?.longitude);
      console.log(`[Créneaux] ✅ ${rdvWithCoords.length} RDV avec coordonnées`);

      setAllRdv(rdvWithCoords);

      // 🚀 RÉDUIRE À 3 JOURS au lieu de 7 pour la performance
      await generateOptimalTimeSlots(rdvWithCoords, commerciauxData);

    } catch (error) {
      console.error('Error calculating optimal time slots:', error);
      toast.error('Erreur lors du calcul des créneaux optimaux');
    } finally {
      setLoading(false);
    }
  };

  const generateOptimalTimeSlots = async (existingRdv, commerciauxData) => {
    const optimalSlots = {};
    const today = new Date();
    
    // 🚀 RÉDUIRE À 3 JOURS au lieu de 7 pour améliorer la performance
    console.log('[Créneaux] 📅 Génération des créneaux pour 3 jours...');
    for (let dayOffset = 0; dayOffset < 3; dayOffset++) {
      const targetDate = new Date(today);
      targetDate.setDate(today.getDate() + dayOffset);
      
      // Skip weekends
      if (targetDate.getDay() === 0 || targetDate.getDay() === 6) {
        continue;
      }
      
      const dateStr = targetDate.toISOString().split('T')[0];
      optimalSlots[dateStr] = await calculateDayOptimalSlots(targetDate, existingRdv, commerciauxData);
    }
    
    console.log('[Créneaux] ✅ Créneaux générés:', Object.keys(optimalSlots).length, 'jours');
    setCreneauxOptimises(optimalSlots);
  };

  const calculateDayOptimalSlots = async (targetDate, existingRdv, commerciauxData) => {
    const dateStr = targetDate.toISOString().split('T')[0];
    const daySlots = [];

    // Récupérer les RDV existants pour cette date
    const existingRdvThisDay = existingRdv.filter(rdv => {
      const rdvDate = new Date(rdv.date_rdv);
      return rdvDate.toDateString() === targetDate.toDateString();
    });

    // Grouper par commercial
    const rdvByCommercial = {};
    existingRdvThisDay.forEach(rdv => {
      if (!rdvByCommercial[rdv.assigned_to]) {
        rdvByCommercial[rdv.assigned_to] = [];
      }
      rdvByCommercial[rdv.assigned_to].push(rdv);
    });

    // Pour chaque commercial, calculer les meilleurs créneaux
    for (const commercial of commerciauxData) {
      const commercialRdv = rdvByCommercial[commercial.id] || [];
      const slots = await calculateCommercialOptimalSlots(
        targetDate, 
        commercialRdv, 
        commercial
      );
      daySlots.push(...slots);
    }

    // Trier par score d'optimisation (meilleur en premier)
    return daySlots.sort((a, b) => b.score - a.score);
  };

  const calculateCommercialOptimalSlots = async (targetDate, commercialRdv, commercial) => {
    const slots = [];
    
    // Heures de travail : 8h-18h
    const workStart = 8;
    const workEnd = 18;
    
    const estimatedDuration = 60; // minutes

    // 🆕 CRÉER UNE LISTE DES PLAGES HORAIRES OCCUPÉES (avec durées)
    const occupiedSlots = commercialRdv.map(rdv => {
      const startTime = new Date(rdv.date_rdv);
      const duration = rdv.duree_minutes || 60;
      const endTime = new Date(startTime.getTime() + duration * 60000);
      
      return {
        start: startTime,
        end: endTime,
        startHour: startTime.getHours(),
        startMinutes: startTime.getMinutes(),
        durationMinutes: duration
      };
    }).sort((a, b) => a.start - b.start);

    // 🚀 GÉOCODER L'ADRESSE DU COMMERCIAL UNE SEULE FOIS (avec cache)
    let commercialHomeCoords = null;
    if (commercial?.adresse_personnelle && commercial?.code_postal_personnel && commercial?.ville_personnelle) {
      const fullAddress = `${commercial.adresse_personnelle}, ${commercial.code_postal_personnel} ${commercial.ville_personnelle}`;
      const coords = await geocodeAddressWithCache(fullAddress);
      if (coords) {
        commercialHomeCoords = coords;
        console.log(`[Créneaux] 🏠 Adresse domicile ${commercial.first_name}:`, commercialHomeCoords);
      }
    }

    // 🆕 FONCTION POUR VÉRIFIER SI UN CRÉNEAU EST DISPONIBLE
    const isSlotAvailable = (proposedStartHour, proposedStartMinutes, durationMinutes) => {
      const proposedStart = new Date(targetDate);
      proposedStart.setHours(proposedStartHour, proposedStartMinutes, 0, 0);
      const proposedEnd = new Date(proposedStart.getTime() + durationMinutes * 60000);

      // Vérifier chevauchement avec chaque RDV existant
      for (const occupied of occupiedSlots) {
        if (proposedStart < occupied.end && proposedEnd > occupied.start) {
          return false;
        }
      }
      return true;
    };

    // Générer les créneaux possibles (par tranches de 30 minutes)
    for (let hour = workStart; hour < workEnd; hour++) {
      for (let minutes of [0, 30]) {
        const proposedStart = new Date(targetDate);
        proposedStart.setHours(hour, minutes, 0, 0);
        const proposedEnd = new Date(proposedStart.getTime() + estimatedDuration * 60000);
        
        if (proposedEnd.getHours() >= workEnd) {
          continue;
        }

        if (!isSlotAvailable(hour, minutes, estimatedDuration)) {
          continue;
        }

        const slotStart = new Date(targetDate);
        slotStart.setHours(hour, minutes, 0, 0);

        // Calculer le score
        const score = await calculateSlotOptimizationScore(
          slotStart, 
          commercialRdv, 
          commercial,
          commercialHomeCoords
        );

        if (score > 0) {
          slots.push({
            datetime: slotStart,
            commercial: commercial,
            score: score,
            details: await getSlotDetails(slotStart, commercialRdv, commercial, commercialHomeCoords),
            estimatedDuration: estimatedDuration
          });
        }
      }
    }

    return slots;
  };

  const calculateSlotOptimizationScore = async (proposedSlot, commercialRdv, commercial, commercialHomeCoords) => {
    if (commercialRdv.length === 0) {
      // Si c'est le premier RDV et qu'on a l'adresse du commercial
      if (commercialHomeCoords) {
        // Calculer distance depuis le domicile
        const travelTimeFromHome = await calculateTravelTime(
          commercialHomeCoords.lat, commercialHomeCoords.lon,
          prospectLatitude, prospectLongitude
        );
        
        // Bonus si proche du domicile (< 30min)
        const proximityBonus = travelTimeFromHome < 30 ? (30 - travelTimeFromHome) : 0;
        return 100 + proximityBonus; // Score de base + bonus proximité
      }
      return 100; // Score parfait si aucun autre RDV
    }

    let totalTravelTime = 0;
    let minTravelTime = Infinity;

    // 🆕 SI PREMIER RDV DE LA JOURNÉE, calculer depuis le domicile
    const sortedRdv = commercialRdv.sort((a, b) => new Date(a.date_rdv) - new Date(b.date_rdv));
    const firstRdv = sortedRdv[0];
    const proposedHour = proposedSlot.getHours();
    const firstRdvHour = new Date(firstRdv.date_rdv).getHours();

    // Si le créneau proposé est AVANT le premier RDV existant
    if (proposedHour < firstRdvHour && commercialHomeCoords) {
      // Calculer : Domicile → Nouveau prospect → Premier RDV existant
      const homeToProspect = await calculateTravelTime(
        commercialHomeCoords.lat, commercialHomeCoords.lon,
        prospectLatitude, prospectLongitude
      );
      
      const prospectToFirstRdv = await calculateTravelTime(
        prospectLatitude, prospectLongitude,
        firstRdv.prospect.latitude, firstRdv.prospect.longitude
      );
      
      totalTravelTime = homeToProspect + prospectToFirstRdv;
      minTravelTime = Math.min(homeToProspect, prospectToFirstRdv);
      
    } else {
      // Calculer le temps de trajet vers tous les RDV existants
      for (const rdv of commercialRdv) {
        const travelTime = await calculateTravelTime(
          prospectLatitude, prospectLongitude,
          rdv.prospect.latitude, rdv.prospect.longitude
        );
        
        totalTravelTime += travelTime;
        minTravelTime = Math.min(minTravelTime, travelTime);
      }
    }

    // Score basé sur la proximité des RDV existants
    const proximityScore = Math.max(0, 100 - minTravelTime * 2);

    // Bonus si le créneau est proche d'autres RDV (regroupement)
    const groupingScore = calculateGroupingScore(proposedSlot, commercialRdv);

    // Score d'efficacité basé sur le temps de trajet moyen
    const avgTravelTime = commercialRdv.length > 0 ? totalTravelTime / commercialRdv.length : 0;
    const efficiencyScore = Math.max(0, 50 - avgTravelTime);

    return proximityScore + groupingScore + efficiencyScore;
  };

  const calculateGroupingScore = (proposedSlot, commercialRdv) => {
    const proposedHour = proposedSlot.getHours();
    const existingHours = commercialRdv.map(rdv => new Date(rdv.date_rdv).getHours());
    
    // Bonus si le créneau est adjacent à d'autres RDV
    const adjacentSlots = existingHours.filter(hour => 
      Math.abs(hour - proposedHour) === 1
    ).length;
    
    return adjacentSlots * 25; // 25 points par créneau adjacent
  };

  const calculateTravelTime = async (lat1, lon1, lat2, lon2) => {
    if (!lat1 || !lon1 || !lat2 || !lon2) return 30; // Valeur par défaut

    // 🚀 OPTIMISATION : Utiliser le calcul approximatif (Haversine) au lieu d'appeler l'API
    // Cela évite des centaines d'appels API et réduit le temps de calcul de 35s à <3s
    const R = 6371; // Rayon de la Terre en km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const distance = R * c;
    
    // Estimation réaliste : 35km/h en moyenne (trafic urbain + périurbain)
    const travelTimeMinutes = Math.round(distance / 35 * 60);
    
    return travelTimeMinutes;
  };

  const getSlotDetails = async (proposedSlot, commercialRdv, commercial, commercialHomeCoords) => {
    const details = {
      nearbyRdv: [],
      totalTravelTime: 0,
      efficiency: 'high',
      fromHome: false,
      homeTravelTime: null
    };

    // 🆕 Si c'est le premier RDV et qu'on a l'adresse du commercial
    const sortedRdv = commercialRdv.sort((a, b) => new Date(a.date_rdv) - new Date(b.date_rdv));
    const proposedHour = proposedSlot.getHours();
    
    if (sortedRdv.length > 0) {
      const firstRdvHour = new Date(sortedRdv[0].date_rdv).getHours();
      
      if (proposedHour < firstRdvHour && commercialHomeCoords) {
        // Ce sera le premier RDV, calculer depuis le domicile
        const homeTravelTime = await calculateTravelTime(
          commercialHomeCoords.lat, commercialHomeCoords.lon,
          prospectLatitude, prospectLongitude
        );
        
        details.fromHome = true;
        details.homeTravelTime = homeTravelTime;
        details.totalTravelTime = homeTravelTime;
      }
    } else if (commercialHomeCoords) {
      // Aucun autre RDV, calculer depuis le domicile
      const homeTravelTime = await calculateTravelTime(
        commercialHomeCoords.lat, commercialHomeCoords.lon,
        prospectLatitude, prospectLongitude
      );
      
      details.fromHome = true;
      details.homeTravelTime = homeTravelTime;
      details.totalTravelTime = homeTravelTime;
    }

    // Trouver les RDV proches dans le temps
    const nearbyRdv = commercialRdv.filter(rdv => {
      const rdvHour = new Date(rdv.date_rdv).getHours();
      return Math.abs(rdvHour - proposedHour) <= 2;
    });

    details.nearbyRdv = await Promise.all(
      nearbyRdv.map(async (rdv) => {
        const travelTime = await calculateTravelTime(
          prospectLatitude, prospectLongitude,
          rdv.prospect.latitude, rdv.prospect.longitude
        );
        
        if (!details.fromHome) {
          details.totalTravelTime += travelTime;
        }
        
        return {
          ...rdv,
          travelTime: travelTime,
          travelDistance: await calculateTravelDistance(
            prospectLatitude, prospectLongitude,
            rdv.prospect.latitude, rdv.prospect.longitude
          )
        };
      })
    );

    // Déterminer l'efficacité
    if (details.totalTravelTime < 30) details.efficiency = 'high';
    else if (details.totalTravelTime < 60) details.efficiency = 'medium';
    else details.efficiency = 'low';

    return details;
  };

  const calculateTravelDistance = async (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return (R * c).toFixed(1);
  };

  const handleSelectCreneau = (creneau) => {
    setSelectedCreneau(creneau);
  };

  const confirmCreneau = () => {
    if (selectedCreneau && onSelectCreneau) {
      onSelectCreneau({
        date_rdv: selectedCreneau.datetime.toISOString(),
        suggested_commercial: selectedCreneau.commercial,
        optimization_details: selectedCreneau.details
      });
      onClose();
    }
  };

  const getEfficiencyColor = (efficiency) => {
    switch (efficiency) {
      case 'high': return 'text-green-600 bg-green-50';
      case 'medium': return 'text-yellow-600 bg-yellow-50';
      case 'low': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getEfficiencyIcon = (efficiency) => {
    switch (efficiency) {
      case 'high': return CheckCircle;
      case 'medium': return Clock;
      case 'low': return AlertTriangle;
      default: return Clock;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-blue-600" />
            Créneaux Optimisés - Intelligence Artificielle
          </DialogTitle>
          <p className="text-sm text-gray-600">
            Analyse de {allRdv.length} RDV existants pour proposer les meilleurs créneaux 
            selon les temps de trajet et l&apos;optimisation des tournées
          </p>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Calcul des créneaux optimaux...</span>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Prospect Address Info */}
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-blue-600" />
                  <span className="font-medium">Nouveau RDV :</span>
                  <span className="text-sm">{prospectAddress}</span>
                </div>
              </CardContent>
            </Card>

            {/* Créneaux par jour */}
            <div className="space-y-4">
              {Object.entries(creneauxOptimises).map(([date, slots]) => (
                <Card key={date} className="border border-gray-200">
                  <CardHeader>
                    <CardTitle className="text-lg">
                      {new Date(date + 'T12:00:00').toLocaleDateString('fr-FR', {
                        weekday: 'long',
                        day: 'numeric',
                        month: 'long'
                      })}
                      <Badge variant="outline" className="ml-2">
                        {slots.length} créneaux disponibles
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                      {slots.slice(0, showAllSlots[date] ? slots.length : 6).map((slot, index) => {
                        const EfficiencyIcon = getEfficiencyIcon(slot.details.efficiency);
                        return (
                          <div
                            key={index}
                            className={`p-3 border rounded-lg cursor-pointer transition-all ${
                              selectedCreneau === slot
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                            onClick={() => handleSelectCreneau(slot)}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <Clock className="w-4 h-4 text-gray-600" />
                                <span className="font-medium">
                                  {slot.datetime.toLocaleTimeString('fr-FR', {
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </span>
                                {/* 🆕 Afficher la durée estimée */}
                                <span className="text-xs text-gray-500">
                                  ({slot.estimatedDuration}min)
                                </span>
                              </div>
                              <Badge className="text-xs" variant="outline">
                                Score: {Math.round(slot.score)}
                              </Badge>
                            </div>

                            <div className="text-sm space-y-1">
                              <div className="flex items-center gap-1">
                                <span className="font-medium">Commercial:</span>
                                <span>{slot.commercial.first_name} {slot.commercial.last_name}</span>
                              </div>

                              <div className={`flex items-center gap-1 px-2 py-1 rounded ${getEfficiencyColor(slot.details.efficiency)}`}>
                                <EfficiencyIcon className="w-3 h-3" />
                                <span className="text-xs font-medium">
                                  Efficacité {slot.details.efficiency === 'high' ? 'Excellente' : 
                                           slot.details.efficiency === 'medium' ? 'Bonne' : 'Faible'}
                                </span>
                              </div>

                              {/* 🆕 Afficher trajet depuis domicile si applicable */}
                              {slot.details.fromHome && (
                                <div className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                                  <div className="flex items-center gap-1">
                                    <Navigation className="w-3 h-3" />
                                    <span className="font-medium">
                                      🏠 Depuis domicile: {slot.details.homeTravelTime}min
                                    </span>
                                  </div>
                                </div>
                              )}

                              {slot.details.nearbyRdv.length > 0 && (
                                <div className="text-xs text-gray-600">
                                  <div className="flex items-center gap-1">
                                    <Route className="w-3 h-3" />
                                    <span>
                                      {slot.details.nearbyRdv.length} RDV proches{!slot.details.fromHome && `, ${slot.details.totalTravelTime}min de trajet`}
                                    </span>
                                  </div>
                                  
                                  {slot.details.nearbyRdv.slice(0, 2).map((nearbyRdv, i) => (
                                    <div key={i} className="flex items-center gap-1 ml-4 mt-1">
                                      <ChevronRight className="w-2 h-2" />
                                      <span>
                                        {nearbyRdv.prospect.prenom} {nearbyRdv.prospect.nom} 
                                        ({nearbyRdv.travelTime}min, {nearbyRdv.travelDistance}km)
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {slots.length > 6 && (
                      <div className="mt-3 text-center">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setShowAllSlots(prev => ({
                            ...prev,
                            [date]: !prev[date]
                          }))}
                        >
                          {showAllSlots[date] 
                            ? 'Masquer les créneaux supplémentaires'
                            : `Voir ${slots.length - 6} autres créneaux`
                          }
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Actions */}
            <div className="flex justify-between pt-4 border-t">
              <Button variant="outline" onClick={onClose}>
                Annuler
              </Button>
              <Button 
                onClick={confirmCreneau}
                disabled={!selectedCreneau}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Confirmer ce créneau
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CreneauxDisponibles;